import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { ComponentProps } from "react";
import { twMerge } from 'tailwind-merge';
import { UseFormRegisterReturn } from 'react-hook-form';

interface Option {
    value: string;
    label: string;
}

type MultiSelectDropdownProps = ComponentProps<"select"> & {
    options: Option[];
    placeholder?: string;
    onChange?: (selected: Option[]) => void;
    required?: boolean;
    labelValue: string;
    register?: UseFormRegisterReturn;
    error?: string
}



interface Option {
    value: string;
    label: string;
}


const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
    options,
    placeholder = "Select options",
    onChange,
    register,
    error,
    required = false,
    labelValue,
}) => {
    const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);

    const handleSelect = (option: Option) => {
        const newSelectedOptions = [...selectedOptions, option];
        setSelectedOptions(newSelectedOptions);
        onChange?.(newSelectedOptions);
        if (register) {
            register.onChange({
                target: {
                    name: register.name,
                    value: newSelectedOptions.map(opt => opt.value)
                }
            });
        }
    };

    const handleRemove = (valueToRemove: string) => {
        const newSelectedOptions = selectedOptions.filter(
            (option) => option.value !== valueToRemove
        );
        setSelectedOptions(newSelectedOptions);
        onChange?.(newSelectedOptions);
        if (register) {
            register.onChange({
                target: {
                    name: register.name,
                    value: newSelectedOptions.map(opt => opt.value)
                }
            });
        }
    };

    const availableOptions = options?.filter(
        (option) => !selectedOptions.some((selected) => selected.value === option.value)
    );

    return (
        <div className="relative w-full" ref={dropdownRef}>
            <label
                className={twMerge(
                    "text-sm text-secondary capitalize ",
                    required && "after:content-['*'] after:pl-1 after:text-rose-800",
                )}
            >
                {labelValue}
            </label>
            
            <input 
                type="hidden" 
                className='placeholder-zinc-300 '
                {...register} 
                value={selectedOptions.map(opt => opt.value).join(',')} 
            />

            <div
                className={twMerge(
                    "flex flex-wrap items-center gap-2 bg-white border border-gray-300 rounded-md px-3 py-2 cursor-pointer focus-within:ring-2 focus-within:ring-primary mt-1",
                    error && "text-rose-800 border-rose-800"
                )}
                onClick={toggleDropdown}
            >
                {selectedOptions.map((option) => (
                    <div
                        key={option.value}
                        className="flex items-center bg-gray-200 text-xs text-gray-700 px-[5px] py-[1px] rounded"
                    >
                        {option.label}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleRemove(option.value);
                            }}
                            className="ml-2 bg-gray-300 rounded-full p-[1.3px]"
                        >
                            <X size={16} className="text-gray-500" />
                        </button>
                    </div>
                ))}
                {selectedOptions.length === 0 && (
                    <span className="text-gray-400 text-sm">{placeholder}</span>
                )}
                <ChevronDown className='absolute right-3' size={16} />
            </div>

            {error && (
                <p className="text-rose-800 border-rose-800 font-semibold text-xs mt-1">{error}</p>
            )}

            {isDropdownOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {availableOptions.length > 0 ? (
                        availableOptions.map((option) => (
                            <div
                                key={option.value}
                                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                                onClick={() => handleSelect(option)}
                            >
                                {option.label}
                            </div>
                        ))
                    ) : (
                        <div className="px-4 py-2 text-sm text-gray-500 text-center">
                            No options available
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default MultiSelectDropdown;